import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { headers } from '../../styles/type';
import { containerStyles } from '../../styles/layout';

import TextRenderer from '../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(80)} 0;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: repeat(1, 1fr);
  gap: ${rem(40)};

  ${({ columnCount }) => (columnCount >= 2 && `
    ${media('>tiny')} {
      grid-template-columns: repeat(2, 1fr);
    }
  `)}

  ${({ columnCount }) => (columnCount === 3 && `
    ${media('>smedium')} {
      grid-template-columns: repeat(3, 1fr)
    }
  `)}

  ${({ columnCount }) => (columnCount === 4 && `
    ${media('>medium')} {
      grid-template-columns: repeat(4, 1fr)
    }
  `)}
`;

const Stat = styled.h2`
  ${headers.h7}
  text-align: center;
  color: ${colors.gray['600']};
`;

const StatData = styled.strong`
  ${headers.h3}
  display: block;
  color: ${colors.blue['800']};
`;

const StatGrid = ({
  item1Headline,
  item1Text,
  item2Headline,
  item2Text,
  item3Headline,
  item3Text,
  item4Headline,
  item4Text,
  backgroundColor
}) => {
  const items = [
    { headline: item1Headline, text: item1Text },
    { headline: item2Headline, text: item2Text },
    { headline: item3Headline, text: item3Text },
    { headline: item4Headline, text: item4Text },
  ];
  const itemCount = items.filter((item) => (item.headline || item.text)).length;

  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper>
        <Grid columnCount={itemCount}>
          {items.map((item, index) => (
            (item.headline || item.text) && (
              // eslint-disable-next-line react/no-array-index-key
              <Stat key={index}>
                {item.headline && (
                  <StatData>
                    <TextRenderer text={item.headline} />
                  </StatData>
                )}
                {item.text && (
                  <TextRenderer text={item.text} />
                )}
              </Stat>
            )
          ))}
        </Grid>
      </ContentWrapper>
    </Container>
  );
};

StatGrid.propTypes = {
  item1Headline: PropTypes.string,
  item1Text: PropTypes.string,
  item2Headline: PropTypes.string,
  item2Text: PropTypes.string,
  item3Headline: PropTypes.string,
  item3Text: PropTypes.string,
  item4Headline: PropTypes.string,
  item4Text: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment StatGridFragment on ContentfulStatGrid {
    id
    __typename
    item1Headline
    item1Text
    item2Headline
    item2Text
    item3Headline
    item3Text
    item4Headline
    item4Text
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default StatGrid;
